import { Navigate, Route, Routes } from 'react-router-dom'
import type { Permission } from '@pharmacia/core'
import { useAuth } from './lib/auth.tsx'
import { AppShell, PageHeader } from './components/AppShell.tsx'
import { SignIn } from './pages/SignIn.tsx'
import { StaffPage } from './pages/Staff.tsx'
import { PatientsPage } from './pages/Patients.tsx'
import { RegisterPage } from './pages/Register.tsx'
import { PatientDetailPage } from './pages/PatientDetail.tsx'
import { EditRecordPage } from './pages/EditRecord.tsx'
import { ProductsPage } from './pages/Products.tsx'
import { PrescriptionsPage } from './pages/Prescriptions.tsx'
import { PrescriptionFormPage } from './pages/PrescriptionForm.tsx'
import { PrescriptionDetailPage } from './pages/PrescriptionDetail.tsx'
import { BillingPage } from './pages/Billing.tsx'
import { BillDetailPage } from './pages/BillDetail.tsx'
import { AuditPage } from './pages/Audit.tsx'
import { AnalyticsPage } from './pages/Analytics.tsx'
import { Empty, Spinner } from './components/ui.tsx'

// Hides a route the person's role cannot use. The API enforces the same
// rule independently -- this only avoids showing a screen that would come
// back empty with a 403.
function Guard({ need, children }: { need: Permission; children: React.ReactNode }) {
  const { can } = useAuth()
  if (!can(need)) {
    return (
      <>
        <PageHeader title="Not available" />
        <div className="content">
          <div className="card"><Empty title="You do not have access to this section">
            Ask the proprietor if you need it.
          </Empty></div>
        </div>
      </>
    )
  }
  return <>{children}</>
}

export function App() {
  const { status, can } = useAuth()

  if (status === 'booting') {
    return <div className="boot"><Spinner /><span>Loading…</span></div>
  }
  if (status === 'signed-out') return <SignIn />

  // Reception and doctors land on patients; an owner with no patient
  // permission would still get somewhere sensible.
  const home = can('patient:read') ? '/patients'
    : can('billing:read') ? '/billing'
    : can('prescription:read') ? '/prescriptions'
    : can('staff:read') ? '/staff' : '/audit'

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={home} replace />} />
        <Route path="/patients" element={<Guard need="patient:read"><PatientsPage /></Guard>} />
        <Route path="/register" element={<Guard need="patient:write"><RegisterPage /></Guard>} />
        <Route path="/patients/:id" element={<Guard need="patient:read"><PatientDetailPage /></Guard>} />
        <Route path="/patients/:id/edit" element={<Guard need="patient:write"><EditRecordPage /></Guard>} />
        <Route path="/prescriptions" element={<Guard need="prescription:read"><PrescriptionsPage /></Guard>} />
        <Route path="/prescriptions/new" element={<Guard need="prescription:write"><PrescriptionFormPage /></Guard>} />
        <Route path="/prescriptions/:id" element={<Guard need="prescription:read"><PrescriptionDetailPage /></Guard>} />
        <Route path="/prescriptions/:id/edit" element={<Guard need="prescription:write"><PrescriptionFormPage /></Guard>} />
        <Route path="/billing" element={<Guard need="billing:read"><BillingPage /></Guard>} />
        <Route path="/billing/:id" element={<Guard need="billing:read"><BillDetailPage /></Guard>} />
        <Route path="/products" element={<Guard need="product:read"><ProductsPage /></Guard>} />
        <Route path="/analytics" element={<Guard need="analytics:read"><AnalyticsPage /></Guard>} />
        <Route path="/staff" element={<Guard need="staff:read"><StaffPage /></Guard>} />
        <Route path="/audit" element={<Guard need="audit:read"><AuditPage /></Guard>} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  )
}
